import { SITE_CONFIG } from "../../app/config";
import { useHead, buildCurationSchema, buildFAQSchema } from "../../seo";
import { curationBySlug } from "./best-data";

type BestPageProps = {
  slug: string;
};

export const BestPage = ({ slug }: BestPageProps): JSX.Element => {
  const curation = curationBySlug.get(slug);

  if (!curation) {
    return (
      <div className="shell">
        <div className="pseo-page">
          <header className="pseo-header">
            <a href="/best" className="blog-back">← All Recommendations</a>
            <h1 className="pseo-title">Page Not Found</h1>
            <p className="pseo-subtitle">
              This recommendation list doesn't exist.{" "}
              <a href="/best">Browse all recommendations →</a>
            </p>
          </header>
        </div>
      </div>
    );
  }

  const curationSchemas = buildCurationSchema({
    title: curation.metaTitle,
    description: curation.metaDescription,
    path: `/best/${curation.slug}`,
  });

  const faqSchema = curation.faq.length > 0 ? buildFAQSchema(curation.faq) : null;
  const jsonLd = faqSchema ? [...curationSchemas, faqSchema] : curationSchemas;

  useHead({
    title: curation.metaTitle,
    description: curation.metaDescription,
    path: `/best/${curation.slug}`,
    ogType: "article",
    jsonLd,
  });

  return (
    <div className="shell">
      <div className="pseo-page">
        <header className="pseo-header">
          <a href="/best" className="blog-back">← All Recommendations</a>
          <h1 className="pseo-title">{curation.title}</h1>
          <p className="pseo-subtitle">{curation.metaDescription}</p>
        </header>

        <article className="best-content">
          {/* Introduction */}
          <div className="best-intro">
            {curation.introduction.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>

          {/* Recommended Items */}
          <div className="best-items">
            {curation.items.map((item, i) => (
              <div key={i} className="best-item-card">
                <div className="best-item-rank">#{i + 1}</div>
                <div className="best-item-content">
                  <h2 className="best-item-name">{item.name}</h2>
                  {item.priceRange && (
                    <span className="best-item-price">{item.priceRange}</span>
                  )}
                  <p className="best-item-desc">{item.description}</p>
                  <div className="best-item-highlights">
                    <h3>Why We Recommend It</h3>
                    <ul>
                      {item.highlights.map((h, j) => (
                        <li key={j}>{h}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="best-item-best-for">
                    <strong>Best for:</strong> {item.bestFor}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Buying Guide Sections */}
          {curation.buyingGuide.map((section, i) => (
            <section key={i} className="best-guide-section">
              <h2>{section.heading}</h2>
              {section.content.map((p, j) => (
                <p key={j}>{p}</p>
              ))}
            </section>
          ))}

          {/* Verdict */}
          <section className="best-verdict">
            <h2>Our Verdict</h2>
            {curation.verdict.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </section>

          {/* FAQ */}
          {curation.faq.length > 0 && (
            <section className="best-faq">
              <h2>Frequently Asked Questions</h2>
              {curation.faq.map((item, i) => (
                <div key={i} className="best-faq-item">
                  <h3>{item.question}</h3>
                  <p>{item.answer}</p>
                </div>
              ))}
            </section>
          )}
        </article>

        <footer className="pseo-footer">
          <div className="blog-cta">
            <h3>Ready to Start Your Collection?</h3>
            <p>
              Paste your Spotify playlist and discover where to buy your favorite albums on vinyl
              and CD at the best prices.
            </p>
            <a href="/" className="blog-cta-button">
              Try {SITE_CONFIG.name} Free →
            </a>
          </div>
          <div className="pseo-nav-links">
            <a href="/best">← All Recommendations</a>
            <a href="/guides">Guides</a>
            <a href="/glossary">Glossary</a>
            <a href="/">Home</a>
          </div>
        </footer>
      </div>
    </div>
  );
};
